import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import type {
  Notification,
  NotificationSettings,
  NotificationFilters,
  NotificationPagination,
  NotificationContextType,
} from '../../../types/types';
import * as notificationService from '../../../services/notificationService';
import { auth } from '../../../lib/api/api';

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

interface NotificationProviderProps {
  children: React.ReactNode;
  autoRefreshInterval?: number; // milliseconds, default 5 minutes
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({
  children,
  autoRefreshInterval = 300000, // 5 minutes
}) => {
  const location = useLocation();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [settings, setSettings] = useState<NotificationSettings | null>(null);

  const refreshIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const isMountedRef = useRef(true);
  const prevPathnameRef = useRef(location.pathname);

  /**
   * Fetch unread count
   */
  const fetchUnreadCount = useCallback(async () => {
    // Skip if user is not authenticated
    if (!auth.getToken()) {
      return;
    }

    try {
      const count = await notificationService.getUnreadCount();
      if (isMountedRef.current) {
        setUnreadCount(count);
      }
    } catch (err: unknown) {
      console.error('Error fetching unread count:', err);
      // Don't set error state for background refreshes
    }
  }, []);

  /**
   * Fetch notifications with filters and pagination
   */
  const fetchNotifications = useCallback(
    async (filters?: NotificationFilters, pagination?: NotificationPagination) => {
      // Skip if user is not authenticated
      if (!auth.getToken()) {
        if (isMountedRef.current) {
          setLoading(false);
          setNotifications([]);
          setUnreadCount(0);
        }
        return;
      }

      try {
        setLoading(true);
        setError(null);

        console.log('[NotificationContext] Fetching notifications with filters:', filters, 'pagination:', pagination);
        const response = await notificationService.getNotifications(filters, pagination);
        console.log('[NotificationContext] Fetched notifications:', response);

        if (isMountedRef.current) {
          setNotifications(response.notifications);
          console.log('[NotificationContext] Set notifications count:', response.notifications.length);
          // Also update unread count
          await fetchUnreadCount();
        }
      } catch (err: unknown) {
        const errObj = err as { response?: { status?: number; data?: { error?: string } }; message?: string };
        const errorMessage = errObj.response?.data?.error || errObj.message || 'Failed to fetch notifications';
        if (isMountedRef.current) {
          setError(errorMessage);
        }
        console.error('[NotificationContext] Error fetching notifications:', err);
        console.error('[NotificationContext] Error details:', { status: errObj.response?.status, data: errObj.response?.data });
      } finally {
        if (isMountedRef.current) {
          setLoading(false);
        }
      }
    },
    [fetchUnreadCount]
  );

  /**
   * Mark a single notification as read
   */
  const markAsRead = useCallback(
    async (notificationId: string) => {
      try {
        // Optimistic update
        setNotifications((prev) =>
          prev.map((n) => (n.id === notificationId ? { ...n, read: true } : n))
        );
        setUnreadCount((prev) => Math.max(0, prev - 1));

        // Actual API call
        await notificationService.markAsRead(notificationId);

        // Refresh to ensure consistency
        await fetchUnreadCount();
      } catch (err: unknown) {
        console.error('Error marking notification as read:', err);
        // Revert optimistic update
        await fetchNotifications();
        throw err;
      }
    },
    [fetchNotifications, fetchUnreadCount]
  );

  /**
   * Mark all notifications as read
   */
  const markAllAsRead = useCallback(async () => {
    try {
      // Optimistic update
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
      setUnreadCount(0);

      // Actual API call
      await notificationService.markAllAsRead();

      // Refresh to ensure consistency
      await fetchNotifications();
    } catch (err: unknown) {
      console.error('Error marking all notifications as read:', err);
      // Revert optimistic update
      await fetchNotifications();
      throw err;
    }
  }, [fetchNotifications]);

  /**
   * Delete a notification
   */
  const deleteNotification = useCallback(
    async (notificationId: string) => {
      try {
        // Optimistic update
        const notification = notifications.find((n) => n.id === notificationId);
        setNotifications((prev) => prev.filter((n) => n.id !== notificationId));
        if (notification && !notification.read) {
          setUnreadCount((prev) => Math.max(0, prev - 1));
        }

        // Actual API call
        await notificationService.deleteNotification(notificationId);
      } catch (err: unknown) {
        console.error('Error deleting notification:', err);
        // Revert optimistic update
        await fetchNotifications();
        throw err;
      }
    },
    [notifications, fetchNotifications]
  );

  /**
   * Refresh notifications
   */
  const refreshNotifications = useCallback(async () => {
    await Promise.all([
      fetchNotifications(),
      fetchUnreadCount(),
    ]);
  }, [fetchNotifications, fetchUnreadCount]);

  /**
   * Update notification settings
   */
  const updateSettings = useCallback(async (newSettings: Partial<NotificationSettings>) => {
    try {
      const updated = await notificationService.updateNotificationSettings(newSettings);
      if (isMountedRef.current) {
        setSettings(updated);
      }
    } catch (err: unknown) {
      console.error('Error updating notification settings:', err);
      throw err;
    }
  }, []);

  /**
   * Fetch initial settings
   */
  const fetchSettings = useCallback(async () => {
    // Skip if user is not authenticated
    if (!auth.getToken()) {
      return;
    }

    try {
      const fetchedSettings = await notificationService.getNotificationSettings();
      if (isMountedRef.current) {
        setSettings(fetchedSettings);
      }
    } catch (err: unknown) {
      console.error('Error fetching notification settings:', err);
    }
  }, []);

  /**
   * Initialize on mount
   */
  useEffect(() => {
    isMountedRef.current = true;

    // Initial fetch
    fetchNotifications();
    fetchUnreadCount();
    fetchSettings();

    // Set up auto-refresh interval for unread count (5 min background refresh)
    if (autoRefreshInterval > 0) {
      refreshIntervalRef.current = setInterval(() => {
        fetchUnreadCount();
      }, autoRefreshInterval);
    }

    return () => {
      isMountedRef.current = false;
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
    };
  }, [fetchNotifications, fetchUnreadCount, fetchSettings, autoRefreshInterval]);

  /**
   * Fetch unread count on navigation (not on initial mount)
   */
  useEffect(() => {
    if (prevPathnameRef.current !== location.pathname) {
      fetchUnreadCount();
      prevPathnameRef.current = location.pathname;
    }
  }, [location.pathname, fetchUnreadCount]);

  const value: NotificationContextType = {
    notifications,
    unreadCount,
    loading,
    error,
    fetchNotifications,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    refreshNotifications,
    settings,
    updateSettings,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

/**
 * Hook to use notification context
 */
export const useNotifications = (): NotificationContextType => {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

export default NotificationContext;
